import React, { useEffect, useState } from "react";
import {
  featureListState,
  featureGroupListObject,
  fetchFeatureGroupList,
  editFeatureData,
  saveFeatureData,
} from "../../store/slices/featureListSlice";
import { RootState, AppDispatch } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { hideToast, showToast } from "../../store/slices/toastSlice";
import {
  resetCreateStatus,
  resetEditStatus,
} from "../../store/slices/createFeatureSlice";
import GridTable from "cvmgmt-cube-grid-table";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { Pagination, Chip } from "cprime-foundational-components";
import { formatFilterValue } from "../../utils/formatFilters";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { featureTableData } from "../../services/RequestAccess/RequestAccess";
import { Stack, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import { useIdToken } from "../../hooks/useAuthSession";

interface FeaturesProps {
  setTabValue: (value: number) => void;
  setOpenEditPanel: (value: boolean) => void;
  setFeatureEdit: (value: boolean) => void;
  setFeatureGrpDropdown: (value: featureGroupListObject[]) => void;
  appId: number;
  isEditable?: boolean;
}

// Custom Status Component to display original text with correct icon
export const StatusCell = ({ row }: { row?: any; column?: any }) => {
  const status = row?.status || "";

  const getStatusConfig = (statusText: string) => {
    if (statusText === "Assigned to Role(s)") {
      return {
        icon: CheckIcon,
        color: "#0E461A",
        chipColor: "#E9F6EC",
      };
    } else if (statusText === "Needs Role Assignment") {
      return {
        icon: WarningIcon,
        color: "#EB8935",
        chipColor: "#FDF0E6",
      };
    }
    // Default fallback
    return {
      icon: CheckIcon,
      color: "#000",
      chipColor: "#F1F2F4",
    };
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <Stack direction="row" alignItems="center" justifyContent="start" gap={1}>
      <Chip
        backgroundColor={config.chipColor}
        color={config.color}
        icon={<Icon />}
        sx={{ textTransform: "capitalize", fontSize: "1rem !important" }}
      />
      <Typography
        sx={{
          textTransform: "capitalize",
          fontSize: "1rem !important",
          height: "100%",
          alignSelf: "center",
        }}
        noWrap
      >
        {status}
      </Typography>
    </Stack>
  );
};

const Features: React.FC<FeaturesProps> = ({
  setTabValue,
  setOpenEditPanel,
  setFeatureEdit,
  setFeatureGrpDropdown,
  appId,
  isEditable,
}) => {
  const { groupData, groupStatus }: featureListState = useSelector(
    (state: RootState) => state.featureList,
  );

  const { createStatus, editStatus } = useSelector(
    (state: RootState) => state.createFeature,
  );

  const tableRefresh = useSelector(
    (state: RootState) => state.apiDetails.tableRefresh,
  );

  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [gridData, setGridData] = useState<GridTableDataType | any>();
  const idToken = useIdToken();

  // Fetch logic
  useEffect(() => {
    setTabValue(1);
  });
  useEffect(() => {
    if (groupStatus === "idle") {
      dispatch(fetchFeatureGroupList());
    } else if (groupStatus === "succeeded") {
      setFeatureGrpDropdown(groupData);
    }
  }, [groupStatus, groupData]);

  // Handle success toast and refresh
  useEffect(() => {
    if (createStatus === "succeeded" || editStatus === "succeeded") {
      const message =
        createStatus === "succeeded"
          ? "Feature created successfully"
          : "Feature updated successfully";

      dispatch(
        showToast({
          message,
          severity: "success",
          actionLabel: "Close",
          handleClose: () => {
            dispatch(hideToast());
            dispatch(
              createStatus === "succeeded"
                ? resetCreateStatus()
                : resetEditStatus(),
            );
          },
        }),
      );

      setOpenEditPanel(false);
      setFeatureEdit(false);
      dispatch(saveFeatureData());
      dispatch(editFeatureData());
    }
  }, [createStatus, editStatus]);

  useEffect(() => {
    fetchData();
  }, [filters, sorting, pagination, tableRefresh]);

  const fetchData = async () => {
    setIsLoading(true);
    const resp = await featureTableData(
      {
        sortingFilter: {
          filter:
            filters?.length > 0
              ? filters.map((item: any) =>
                  formatFilterValue(item.value, item.id),
                )
              : [],
          sort: sorting.map((item: any) => {
            return { columnName: item.id, type: item.desc ? "DESC" : "ASC" };
          }),
          page: {
            limit: pagination.rowsPerPage,
            offset: pagination?.page * pagination?.rowsPerPage,
          },
        },
      },
      appId,
    );

    // Keep original status values, update column type to COMPONENT if needed
    const mappedData = {
      ...resp?.data,
      columns: resp?.data?.columns?.map((col: any) =>
        col.id === "status"
          ? {
              ...col,
              type: "COMPONENT",
              size: col?.size ?? 200,
              minSize: col?.minSize ?? 200,
            }
          : { ...col, size: col?.size ?? 200, minSize: col?.minSize ?? 200 },
      ),
    };

    setGridData(mappedData);
    setIsLoading(false);
  };

  return (
    <>
      {idToken && (
        <>
          <GridTable
            style={{
              height: `calc(100vh - ${!isEditable ? 36.2 : 34}rem)`,
              maxWidth: "calc(100vw - 4rem)",
            }}
            refresh={tableRefresh}
            customPayload={true}
            isCustomizeColumns={false}
            title="Feature List"
            data={gridData}
            isPagination={true}
            isFiltering={true}
            isSorting={true}
            filters={filters}
            sorting={sorting}
            setSorting={setSorting}
            setFilters={setFilters}
            cellActions={[
              {
                event: "status",
                component: <StatusCell />,
              },
              {
                event: "Edit",
                disabled: !isEditable,
                callback: (_event, data) => {
                  const row = data?.row;
                  setOpenEditPanel(true);
                  setFeatureEdit(true);
                  dispatch(editFeatureData(row?.original));
                  const saveFeature = {
                    id: row?.original?.id,
                    feature_name: row?.original?.feature_name,
                    feature_description: row?.original?.feature_description,
                    group_id: 0,
                    group_name: row?.original?.group_name,
                    app_id: appId,
                    permissionId: [row?.original?.permission_ids],
                    visibleToRoles: row?.original?.is_open_to_all,
                    allowedRoles: [row?.original?.role_ids],
                  };
                  dispatch(saveFeatureData(saveFeature));
                },
              },
            ]}
            onRowClick={(_, row: any) => {}}
            loading={isLoading}
            showLoader={true}
          />
          <Pagination
            sx={{ width: "100%" }}
            {...{
              ...pagination,
              count: gridData?.totalCount ?? 0,
              rowsPerPageOptions: [25, 50, 100, 250],
              onPageChange: (e, number) => {
                setPagination({ ...pagination, page: number });
              },
              onRowsPerPageChange: (evt) => {
                setPagination({
                  page: 0,
                  rowsPerPage: Number(evt.target.value),
                });
              },
            }}
          />
        </>
      )}
    </>
  );
};

export default Features;
